<script lang="ts">
  import clsx from 'clsx';
  import {
    HeadingLarge,
    HeadingMedium,
    HeadingSmall,
    HeadingXSmall,
    Paragraph,
  } from '@stickerapp-org/athena/Text';
  import { TextLink } from '@stickerapp-org/athena/TextLink';
  import { Stack, StackItem } from '@stickerapp-org/athena/Stack';
  import { Blockquote } from '@stickerapp-org/athena/Blockquote';
  import { UnorderedList, ListItem } from '@stickerapp-org/athena/UnorderedList';
  import { generateHeadingId } from '@stickerapp-org/athena/TableOfContents';
  import { StoryblokComponent } from '@storyblok/svelte';
  import type {
    StyledMark,
    RichtextItemMarks,
    RichtextContent,
    LinkMark,
    RichtextText,
  } from '@storyblok-types';
  import { type RichTextProps, isRichTextEmpty, isLinkMarkWithValidUrl } from './types';
  import { extractText, isHeading, isRichTextParentType } from './utils';
  const { text, size, color, class: className }: RichTextProps = $props();

  function classNameFromMarks(marks: RichtextItemMarks[]) {
    const classes = [];
    const markTypes = marks.map((m) => m.type);
    if (markTypes.includes('styled')) {
      const styled = marks.find((m) => m.type === 'styled') as StyledMark;
      if (styled.attrs?.class) {
        classes.push(styled.attrs.class);
      }
    }

    if (markTypes.includes('bold')) {
      classes.push('font-bold');
    }
    if (markTypes.includes('italic')) {
      classes.push('italic');
    }
    if (markTypes.includes('strike')) {
      classes.push('line-through');
    }
    if (markTypes.includes('underline')) {
      classes.push('underline');
    }
    if (markTypes.includes('superscript')) {
      classes.push('relative text-[75%] top-[-0.5em]');
    }
    if (markTypes.includes('subscript')) {
      classes.push('relative text-[75%] bottom-[-0.25em]');
    }
    return clsx(classes);
  }

  const headingClasses = '-mb-2 mt-3 first:mt-0 first:mb-0';
</script>

{#snippet item(node: RichtextContent)}
  {#if node.type === 'blok'}
    {#each node.attrs.body as blok (blok._uid)}
      <StoryblokComponent {blok} />
    {/each}
  {:else if node.type === 'image'}
    <img src={node.attrs.src} alt={node.attrs.alt} />
  {:else if node.type === 'paragraph'}
    <Paragraph class={className} {size} {color}>
      {@render childContent(node)}
    </Paragraph>
  {:else if node.type === 'blockquote'}
    <Blockquote class={className}>
      {@render childContent(node)}
    </Blockquote>
  {:else if node.type === 'heading' && node.attrs.level === 1}
    <HeadingLarge class={className} id={generateHeadingId(extractText(node.content))}>
      {@render childContent(node)}
    </HeadingLarge>
  {:else if node.type === 'heading' && node.attrs.level === 2}
    <HeadingMedium class={className} id={generateHeadingId(extractText(node.content))}>
      {@render childContent(node)}
    </HeadingMedium>
  {:else if node.type === 'heading' && node.attrs.level === 3}
    <HeadingSmall class={className} id={generateHeadingId(extractText(node.content))}>
      {@render childContent(node)}
    </HeadingSmall>
  {:else if node.type === 'heading'}
    <HeadingXSmall class={className} id={generateHeadingId(extractText(node.content))}>
      {@render childContent(node)}
    </HeadingXSmall>
  {:else if node.type === 'text'}
    {@render textContent(node.marks?.find((m) => m.type === 'link') as LinkMark | undefined, node)}
  {:else if node.type === 'hard_break'}
    <br />
  {:else if node.type === 'bullet_list'}
    <UnorderedList>
      {#key node.content}
        {#each node.content as content}
          <ListItem>{@render childContent(content)}</ListItem>
        {/each}
      {/key}
    </UnorderedList>
  {:else if node.type === 'ordered_list'}
    <ol class="ml-small">
      {#key node.content}
        {#each node.content as content}
          <li class="ml-gutter list-decimal pl-small">{@render childContent(content)}</li>
        {/each}
      {/key}
    </ol>
  {/if}
{/snippet}

{#snippet textContent(link: LinkMark | undefined, node: RichtextText)}
  {#if node.marks}
    {#if link}
      <TextLink
        href={(isLinkMarkWithValidUrl(link) ? link.computedUrl : link.attrs.href) +
          (link.attrs.anchor ? '#' + link.attrs.anchor : '')}
        isExternal={link.attrs.target === '_blank' ||
          link.attrs.href.startsWith('https://') ||
          link.attrs.href.startsWith('http://')}
        class={classNameFromMarks(node.marks)}
      >
        {node.text}
      </TextLink>
    {:else}
      <span class={classNameFromMarks(node.marks)}>
        {node.text}
      </span>
    {/if}
  {:else}
    {node.text}
  {/if}
{/snippet}

{#snippet childContent(node: RichtextContent)}
  {#if isRichTextParentType(node)}
    {#key node.content}
      {#each node.content ?? [] as content}
        {@render item(content)}
      {/each}
    {/key}
  {/if}
{/snippet}

{#if text && !isRichTextEmpty(text)}
  <Stack space="gutter" class="text-container">
    {#key text.content}
      {#each text.content as content}
        <StackItem class={clsx(isHeading(content) && headingClasses)}>
          {@render item(content)}
        </StackItem>
      {/each}
    {/key}
  </Stack>
{/if}
